// src/components/Testimonials.tsx
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Star, Quote } from 'lucide-react';
import mainsdargentLogo from '../assets/avis_mainsdargent.jpeg';
import mescolleguesLogo from '../assets/avis_mescollegues.jpg';
import aktusLogo from '../assets/avis_aktus.png';
import permanessLogo from '../assets/avis_permaness.png';
import wibeoLogo from '../assets/avis_wibeo.png';
import sospc11Logo from '../assets/avis_sospc11.png';

interface TestimonialData {
  name: string;
  role: string;
  organization?: string;
  content: string;
  logo: string;
  logoImage?: string;
  roundedLogo?: boolean;
  website?: string;
  startDate: string;
}

function getAccompagnementDuration(startDateStr: string): string {
  const [day, month, year] = startDateStr.split('/').map(Number);
  const startDate = new Date(year, month - 1, day);
  const today = new Date();

  let totalMonths = (today.getFullYear() - startDate.getFullYear()) * 12;
  totalMonths += today.getMonth() - startDate.getMonth();

  if (today.getDate() < startDate.getDate()) {
    totalMonths--;
  }

  if (totalMonths < 1) {
    return 'Accompagnement depuis quelques semaines';
  }

  if (totalMonths < 24) {
    return `Accompagnement depuis ${totalMonths} mois`;
  }

  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;

  if (remainingMonths === 0) {
    return `Accompagnement depuis ${years} an${years > 1 ? 's' : ''}`;
  } else if (remainingMonths >= 6) {
    return `Accompagnement depuis ${years + 1} ans`;
  } else {
    return `Accompagnement depuis ${years} an${years > 1 ? 's' : ''} et demi`;
  }
}

interface TestimonialCardProps {
  testimonial: TestimonialData;
  onReadMore: () => void;
  isActive?: boolean;
}

interface ModalProps {
  testimonial: TestimonialData | null;
  isOpen: boolean;
  onClose: () => void;
}

function TestimonialModal({ testimonial, isOpen, onClose }: ModalProps) {
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => setIsAnimatingIn(true), 10);
    } else {
      document.body.style.overflow = 'unset';
      requestAnimationFrame(() => setIsAnimatingIn(false));
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimatingIn(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isOpen || !testimonial) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isAnimatingIn ? 'opacity-100' : 'opacity-0'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="absolute inset-0 bg-black/50 transition-opacity duration-300"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div className={`relative bg-white dark:bg-warm-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-all duration-300 ease-out ${isAnimatingIn ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-warm-gray-800 dark:bg-warm-gray-600 text-white rounded-md hover:bg-aurore-dark transition-colors duration-300"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          <div className="flex items-center space-x-1 mb-6">
            {[...Array(5)].map((_, index) => (
              <Star
                key={index}
                className="w-6 h-6 fill-current text-aurore-dark"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: isAnimatingIn ? 'floatUp 0.6s ease-out forwards' : 'none'
                }}
              />
            ))}
          </div>

          <p id="modal-title" className="text-warm-gray-700 dark:text-warm-gray-300 text-lg leading-relaxed mb-8">
            {testimonial.content}
          </p>

          <div className="flex items-center space-x-4 pt-6 border-t border-warm-gray-200 dark:border-warm-gray-600">
            <div
              className={`w-20 h-20 flex items-center justify-center overflow-hidden ${testimonial.roundedLogo ? 'rounded-full' : 'rounded-xl'}`}
              style={{ backgroundColor: testimonial.logoImage ? '#ffffff' : '#B8806F' }}
            >
              {testimonial.logoImage ? (
                <img
                  src={testimonial.logoImage}
                  alt={`${testimonial.organization || testimonial.name} logo`}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                />
              ) : (
                <span className="text-2xl font-bold text-white">{testimonial.logo}</span>
              )}
            </div>
            <div>
              <p className="font-semibold text-warm-gray-900 dark:text-warm-gray-100">{testimonial.name}</p>
              <p className="text-sm text-warm-gray-500 dark:text-warm-gray-400">{testimonial.role}</p>
              {testimonial.website ? (
                <a
                  href={testimonial.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium hover:underline transition-all duration-300 inline-block text-aurore-dark dark:text-aurore-light"
                >
                  {testimonial.organization}
                </a>
              ) : testimonial.organization ? (
                <p className="text-sm font-medium text-aurore-dark dark:text-aurore-light">{testimonial.organization}</p>
              ) : (
                <p className="text-xs text-aurore-dark dark:text-aurore-light mt-1">{getAccompagnementDuration(testimonial.startDate)}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TestimonialCard({ testimonial, onReadMore, isActive = false }: TestimonialCardProps) {
  const truncatedContent = testimonial.content.length > 120
    ? testimonial.content.substring(0, 120) + '...'
    : testimonial.content;

  return (
    <div className={`bg-white dark:bg-warm-gray-800 rounded-2xl p-6 md:p-8 flex flex-col justify-between items-center text-center h-[420px] transition-all duration-500 overflow-hidden ${isActive ? 'scale-100 opacity-100 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.3)]' : 'scale-[0.85] opacity-[0.5] shadow-[0_4px_15px_rgb(0,0,0,0.05)] dark:shadow-[0_4px_15px_rgb(0,0,0,0.18)]'}`}>
      <div
        className={`w-24 h-24 mb-6 flex items-center justify-center overflow-hidden transition-all duration-300 ${
          testimonial.roundedLogo ? 'rounded-full' : 'rounded-xl'
        }`}
        style={{ backgroundColor: testimonial.logoImage ? '#ffffff' : undefined }}
      >
        {testimonial.logoImage ? (
          <img
            src={testimonial.logoImage}
            alt={`${testimonial.organization || testimonial.name} logo`}
            className="w-full h-full object-contain p-2"
            loading="lazy"
          />
        ) : (
          <span className="text-2xl font-bold text-aurore-dark dark:text-aurore-light">{testimonial.logo}</span>
        )}
      </div>

      <Quote className="w-8 h-8 text-aurore-medium/40 dark:text-aurore-medium/30 mb-4" aria-hidden="true" />

      <div className="flex-1 mb-6">
        <p className="text-warm-gray-700 dark:text-warm-gray-300 text-base leading-relaxed mb-3 line-clamp-4">
          "{truncatedContent}"
        </p>
        {testimonial.content.length > 120 && (
          <button
            onClick={onReadMore}
            className="text-sm font-medium hover:underline transition-all duration-300 uppercase text-aurore-dark dark:text-aurore-light"
          >
            Lire plus
          </button>
        )}
      </div>

      <div className="border-t border-warm-gray-200 dark:border-warm-gray-700 pt-4 w-full">
        <p className="font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-1">{testimonial.name}</p>
        <p className="text-sm text-warm-gray-500 dark:text-warm-gray-400 mb-1">{testimonial.role}</p>
        {testimonial.website ? (
          <a
            href={testimonial.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium hover:underline transition-all duration-300 inline-block text-aurore-dark dark:text-aurore-light"
          >
            {testimonial.organization}
          </a>
        ) : testimonial.organization ? (
          <p className="text-sm font-medium text-aurore-dark dark:text-aurore-light">{testimonial.organization}</p>
        ) : (
          <p className="text-xs text-aurore-dark dark:text-aurore-light mt-1">{getAccompagnementDuration(testimonial.startDate)}</p>
        )}
      </div>
    </div>
  );
}

interface InfiniteCarouselProps<T> {
  items: T[];
  renderItem: (item: T, isActive: boolean) => React.ReactNode;
  keyExtractor: (item: T, index: number) => string;
  autoplay?: boolean;
  autoplayDelay?: number;
  isPaused?: boolean;
}

function InfiniteCarousel<T>({
  items,
  renderItem,
  keyExtractor,
  autoplay = true,
  autoplayDelay = 5000,
  isPaused = false
}: InfiniteCarouselProps<T>) {
  const n = items.length;
  const REPEATS = 5;
  const BASE_INDEX = Math.floor(REPEATS / 2) * n;
  const TOTAL_SLIDES = REPEATS * n;

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(BASE_INDEX);
  const isAnimatingRef = useRef(false);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const visibleSlides = useMemo(() => {
    if (containerWidth < 640) return 1;
    if (containerWidth < 768) return 1.2;
    if (containerWidth < 1024) return 2;
    if (containerWidth < 1280) return 3.5;
    return 4.5;
  }, [containerWidth]);

  const slideWidthPct = 100 / visibleSlides;
  const centerOffsetPct = 50 - slideWidthPct / 2;

  const activeRealIndex = ((displayIndex - BASE_INDEX) % n + n) % n;

  const updateTransform = useCallback((index: number, animate: boolean) => {
    if (!trackRef.current) return;
    trackRef.current.style.transition = animate ? 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
    const translateX = -(index * slideWidthPct) + centerOffsetPct;
    trackRef.current.style.transform = `translateX(${translateX}%)`;
  }, [slideWidthPct, centerOffsetPct]);

  const goToIndex = useCallback((index: number, animate: boolean) => {
    setDisplayIndex(index);
    updateTransform(index, animate);
  }, [updateTransform]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateWidth = () => {
      setContainerWidth(el.clientWidth);
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useLayoutEffect(() => {
    updateTransform(BASE_INDEX, false);
  }, [BASE_INDEX, containerWidth, updateTransform]);

  const next = useCallback(() => {
    if (isAnimatingRef.current || n === 0) return;
    isAnimatingRef.current = true;

    const newIndex = displayIndex + 1;
    goToIndex(newIndex, true);

    if (newIndex >= TOTAL_SLIDES - 2) {
      setTimeout(() => {
        const resetIndex = BASE_INDEX + ((newIndex - BASE_INDEX) % n);
        goToIndex(resetIndex, false);
        isAnimatingRef.current = false;
      }, 500);
    } else {
      setTimeout(() => {
        isAnimatingRef.current = false;
      }, 500);
    }
  }, [displayIndex, n, TOTAL_SLIDES, BASE_INDEX, goToIndex]);

  const prev = useCallback(() => {
    if (isAnimatingRef.current || n === 0) return;
    isAnimatingRef.current = true;

    const newIndex = displayIndex - 1;
    goToIndex(newIndex, true);

    if (newIndex <= 1) {
      setTimeout(() => {
        const resetIndex = BASE_INDEX - n + ((newIndex - BASE_INDEX) % n + n) % n;
        goToIndex(resetIndex, false);
        isAnimatingRef.current = false;
      }, 500);
    } else {
      setTimeout(() => {
        isAnimatingRef.current = false;
      }, 500);
    }
  }, [displayIndex, n, BASE_INDEX, goToIndex]);

  useEffect(() => {
    if (!autoplay || isPaused || n === 0) {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
      return;
    }

    autoplayRef.current = setInterval(() => {
      next();
    }, autoplayDelay);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, [autoplay, isPaused, n, autoplayDelay, next]);

  const duplicatedItems = useMemo(() => {
    const result: { item: T; key: string }[] = [];
    for (let r = 0; r < REPEATS; r++) {
      items.forEach((item, i) => {
        result.push({ item, key: `carousel-${r}-${keyExtractor(item, i)}` });
      });
    }
    return result;
  }, [items, keyExtractor]);

  return (
    <div className="relative">
      <div ref={containerRef} className="overflow-hidden">
        <div
          ref={trackRef}
          className="flex"
        >
          {duplicatedItems.map(({ item, key }, i) => (
            <div
              key={key}
              className="flex-shrink-0 px-3 mb-10"
              style={{ width: `${slideWidthPct}%` }}
            >
              {renderItem(item, i === displayIndex)}
            </div>
          ))}
        </div>
      </div>

      <button
        className="testimonials-nav testimonials-nav-prev"
        aria-label="Témoignage précédent"
        type="button"
        onClick={prev}
      >
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-aurore-dark/90 dark:bg-aurore-medium/80 flex items-center justify-center shadow-md hover:bg-aurore-medium hover:shadow-lg hover:scale-110 transition-all duration-300">
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white transition-transform duration-300 hover:-translate-x-0.5" />
        </div>
      </button>

      <button
        className="testimonials-nav testimonials-nav-next"
        aria-label="Témoignage suivant"
        type="button"
        onClick={next}
      >
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-aurore-dark/90 dark:bg-aurore-medium/80 flex items-center justify-center shadow-md hover:bg-aurore-medium hover:shadow-lg hover:scale-110 transition-all duration-300">
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white transition-transform duration-300 hover:translate-x-0.5" />
        </div>
      </button>

      <div className="testimonials-pagination" role="tablist" aria-label="Navigation des témoignages">
        {items.map((_, i) => (
          <button
            key={`dot-${i}`}
            type="button"
            role="tab"
            aria-selected={i === activeRealIndex}
            aria-label={`Témoignage ${i + 1}`}
            className={`swiper-pagination-bullet ${i === activeRealIndex ? 'swiper-pagination-bullet-active' : ''}`}
            onClick={() => {
              if (isAnimatingRef.current) return;
              isAnimatingRef.current = true;
              const diff = i - activeRealIndex;
              const newIndex = displayIndex + diff;
              goToIndex(newIndex, true);
              setTimeout(() => {
                isAnimatingRef.current = false;
              }, 500);
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [selectedTestimonial, setSelectedTestimonial] = useState<TestimonialData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const testimonials: TestimonialData[] = [
    {
      name: 'Alexis, Gérant',
      role: 'Gérant',
      organization: 'Wibeo',
      content: "Travailler avec AuroreRH a été un vrai soulagement pour notre structure. En tant qu'agence web, nous avions besoin d'un support RH fiable et réactif. Mission accomplie !",
      logo: 'W',
      logoImage: wibeoLogo,
      website: 'https://wibeo.fr/',
      startDate: '01/01/2024',
    },
    {
      name: 'Directeur technique',
      role: 'Direction',
      organization: 'SOSPC11',
      content: "AuroreRH nous accompagne ponctuellement sur la gestion administrative et la paie. Son intervention est toujours professionnelle, efficace et nous permet de nous concentrer sur notre cœur de métier.",
      logo: 'S',
      logoImage: sospc11Logo,
      website: 'https://sospc11.fr/',
      startDate: '04/11/2021',
    },
    {
      name: 'Héloïse Lamotte',
      role: 'Dirigeante',
      organization: "Mains d'argent",
      content: "Nous avons fait appel à AuroreRH pour nous aider à recruter les bons profils, et son efficacité a été immédiate. Disponible, à l'écoute et proactive, je recommande vivement.",
      logo: 'H',
      logoImage: mainsdargentLogo,
      roundedLogo: true,
      startDate: '01/09/2023',
    },
    {
      name: 'Lisa-May et Aurélie',
      role: 'Co-fondatrices',
      organization: 'Mes-Collegues',
      content: "AuroreRH est intervenue ponctuellement pour renforcer notre équipe sur la gestion clients. Disponible, efficace et professionnelle, elle nous a permis de gagner un temps précieux.",
      logo: 'M',
      logoImage: mescolleguesLogo,
      startDate: '01/03/2024',
    },
    {
      name: 'Kaci Boudjema',
      role: 'Dirigeant',
      organization: 'Aktus',
      content: "Nous avons fait appel à AuroreRH pour nous accompagner dans le recrutement de profils très différents. Son professionnalisme et sa réactivité ont été des atouts majeurs.",
      logo: 'K',
      logoImage: aktusLogo,
      startDate: '01/06/2022',
    },
    {
      name: 'Dorian Terrones',
      role: 'Coach sportif indépendant',
      organization: 'Permaness',
      content: "En tant que coach sportif indépendant, je voulais être bien encadré administrativement, sans y passer des heures. AuroreRH a été la solution idéale, claire et rassurante.",
      logo: 'D',
      logoImage: permanessLogo,
      roundedLogo: true,
      startDate: '15/01/2024',
    },
  ];

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-warm-gray-50 dark:bg-warm-gray-900 py-20 md:py-28 lg:py-36"
      aria-labelledby="testimonials-heading"
    >
      <div className="w-full">
        <div className={`container-padding text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-aurore-pale dark:bg-warm-gray-800 rounded-full mb-6">
            <Quote className="w-4 h-4 text-aurore-dark" aria-hidden="true" />
            <span className="text-sm font-medium text-aurore-dark dark:text-aurore-light">Témoignages</span>
          </div>
          <h2 id="testimonials-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-warm-gray-900 dark:text-warm-gray-100 leading-tight mb-6">
            Ils m'ont fait confiance
          </h2>
          <p className="inline-block text-lg md:text-xl text-warm-gray-800 dark:text-warm-gray-100 leading-relaxed max-w-2xl mx-auto bg-aurore-pale/80 dark:bg-warm-gray-800 rounded-full px-6 py-3 mt-4 border border-aurore-light/30 dark:border-warm-gray-700">
            Des collaborations qui durent, parce qu'elles fonctionnent.
          </p>
        </div>

        <div className={`relative transition-all duration-1000 delay-300 mt-20 md:mt-24 lg:mt-28 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <style>{`
            .testimonials-swiper {
              width: 100%;
              padding-block: 80px;
              padding-inline: 0;
            }

            @media screen and (min-width: 768px) {
              .testimonials-swiper {
                -webkit-mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
                mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
              }
            }

            .testimonials-pagination {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              padding-top: 80px;
            }

            .testimonials-pagination .swiper-pagination-bullet {
              width: 8px;
              height: 8px;
              border-radius: 99px;
              background: #B8806F;
              opacity: 0.3;
              transition: all 0.3s ease-out;
              cursor: pointer;
              border: none;
              padding: 0;
            }

            .testimonials-pagination .swiper-pagination-bullet-active {
              width: 30px;
              opacity: 1;
            }

            .testimonials-nav {
              position: absolute;
              z-index: 30;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.3s ease-out;
              background: transparent;
              border: none;
              padding: 0;
              pointer-events: auto;
            }

            .testimonials-nav > div {
              pointer-events: none;
            }

            @media screen and (max-width: 767.9px) {
              .testimonials-nav {
                top: 40%;
                transform: translateY(-50%);
              }

              .testimonials-nav:hover {
                transform: translateY(-50%);
              }
            }

            @media screen and (min-width: 768px) {
              .testimonials-nav {
                top: 50%;
                transform: translateY(-50%);
              }

              .testimonials-nav:hover {
                transform: translateY(-50%) scale(1.1);
              }
            }

            .testimonials-nav-prev {
              left: 20px;
            }

            .testimonials-nav-next {
              right: 20px;
            }

            @media screen and (min-width: 768px) {
              .testimonials-nav-prev {
                left: 40px;
              }

              .testimonials-nav-next {
                right: 40px;
              }
            }

            @media screen and (min-width: 1280px) {
              .testimonials-nav-prev {
                left: 60px;
              }

              .testimonials-nav-next {
                right: 60px;
              }
            }

            @keyframes floatUp {
              0% { transform: translateY(10px); opacity: 0; }
              100% { transform: translateY(0); opacity: 1; }
            }
          `}</style>

          <InfiniteCarousel
            items={testimonials}
            keyExtractor={(item) => item.name}
            renderItem={(item, isActive) => (
              <TestimonialCard
                testimonial={item}
                onReadMore={() => setSelectedTestimonial(item)}
                isActive={isActive}
              />
            )}
            autoplay={true}
            autoplayDelay={5000}
            isPaused={selectedTestimonial !== null}
          />
        </div>

        <div className="container-wide section-padding pt-20 md:pt-28 lg:pt-36">
          <div className="max-w-2xl mx-auto text-center p-8 rounded-xl bg-aurore-pale dark:bg-warm-gray-800 border border-aurore-light dark:border-warm-gray-700">
            <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
              La plupart de mes clients travaillent avec moi depuis plus de deux ans.
              Ce n'est pas un hasard : quand ça fonctionne, on continue.
              Et si ça ne fonctionne pas, on se le dit honnêtement.
            </p>
          </div>
        </div>
      </div>

      <TestimonialModal
        testimonial={selectedTestimonial}
        isOpen={selectedTestimonial !== null}
        onClose={() => setSelectedTestimonial(null)}
      />
    </section>
  );
}
